// react
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useForm } from '@tanstack/react-form';

// material ui
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

// api
import { useDataTypesGetCollectionQuery } from '@/api/generated/recalc/endpoints/v1/datatypes';
import type {
  DataInterval,
  GetCapabilityBaseDto,
  GetDataIntervalConfigurationCapabilityDto,
  GetMBusDataChannelDto,
  GetMBusSensorCapabilityDto,
  PutMBusDataChannelDto,
} from '@/api/generated/recalc/endpoints/v2/devices';
import {
  usePutDataIntervalConfigurationMutation,
  usePutMBusDataChannelMutation,
  usePutMBusMutation,
} from '@/api/generated/recalc/endpoints/v2/devices';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import type { CapabilityPolicy } from './deviceCapabilityRegistry';

type CapabilityEditorProps<TCapability = GetCapabilityBaseDto> = {
  deviceId?: number;
  capability: TCapability;
  onSubmitted?: () => void;
};

type CapabilityEditorRendererProps = {
  deviceId?: number;
  capability: GetCapabilityBaseDto;
  policy: CapabilityPolicy;
  onSubmitted?: () => void;
};

const DATA_INTERVAL_OPTIONS: DataInterval[] = [
  'Second',
  'Minute',
  'Minutes5',
  'Minutes30',
  'Seconds20Boost',
  'Seconds3Boost',
  'Inactive',
  'Custom',
];

type MBusDataChannelFormValues = {
  targetDataType: string;
  conversionFactor: string;
  conversionOffset: string;
};

function DeviceActionFormLayout({
  onSubmit,
  isSubmitting,
  isSubmitDisabled,
  submitLabel,
  children,
}: {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  submitLabel: string;
  children: ReactNode;
}) {
  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        {children}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || isSubmitDisabled}
          >
            {submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function getInitialDataInterval(
  capability: GetDataIntervalConfigurationCapabilityDto,
): DataInterval {
  const candidate = capability.targetIntervalMode ?? capability.intervalMode;

  if (!candidate) {
    return 'Minute';
  }

  return DATA_INTERVAL_OPTIONS.includes(candidate as DataInterval)
    ? (candidate as DataInterval)
    : 'Minute';
}

function getInitialMBusDataChannelValues(
  channel: GetMBusDataChannelDto,
): MBusDataChannelFormValues {
  return {
    targetDataType: channel.targetDataType ?? '',
    conversionFactor:
      channel.conversionFactor != null ? String(channel.conversionFactor) : '',
    conversionOffset:
      channel.conversionOffset != null ? String(channel.conversionOffset) : '',
  };
}

function buildMBusDataChannelPayload(
  values: MBusDataChannelFormValues,
): PutMBusDataChannelDto {
  const payload: PutMBusDataChannelDto = {};

  if (values.targetDataType.trim()) {
    payload.targetDataType = values.targetDataType.trim();
  }

  if (values.conversionFactor.trim()) {
    payload.conversionFactor = Number(values.conversionFactor);
  }

  if (values.conversionOffset.trim()) {
    payload.conversionOffset = Number(values.conversionOffset);
  }

  return payload;
}

function normalizeMBusDataChannelValues(values: MBusDataChannelFormValues) {
  return {
    targetDataType: values.targetDataType.trim(),
    conversionFactor:
      values.conversionFactor.trim() === ''
        ? null
        : Number(values.conversionFactor),
    conversionOffset:
      values.conversionOffset.trim() === ''
        ? null
        : Number(values.conversionOffset),
  };
}

function hasMBusDataChannelDiff(
  currentValues: MBusDataChannelFormValues,
  initialValues: MBusDataChannelFormValues,
) {
  const current = normalizeMBusDataChannelValues(currentValues);
  const initial = normalizeMBusDataChannelValues(initialValues);

  return (
    current.targetDataType !== initial.targetDataType ||
    current.conversionFactor !== initial.conversionFactor ||
    current.conversionOffset !== initial.conversionOffset
  );
}

function normalizeMBusCapabilityValues(values: {
  wireless: boolean;
  mBusDeviceId: string;
  encryptionKey: string;
}) {
  return {
    wireless: values.wireless,
    mBusDeviceId: values.mBusDeviceId.trim(),
    encryptionKey: values.encryptionKey.trim(),
  };
}

function hasMBusCapabilityDiff(
  currentValues: {
    wireless: boolean;
    mBusDeviceId: string;
    encryptionKey: string;
  },
  initialValues: {
    wireless: boolean;
    mBusDeviceId: string;
    encryptionKey: string;
  },
) {
  const current = normalizeMBusCapabilityValues(currentValues);
  const initial = normalizeMBusCapabilityValues(initialValues);

  return (
    current.wireless !== initial.wireless ||
    current.mBusDeviceId !== initial.mBusDeviceId ||
    current.encryptionKey !== initial.encryptionKey
  );
}

function MBusDataChannelEditor({
  deviceId,
  channel,
}: {
  deviceId?: number;
  channel: GetMBusDataChannelDto;
}) {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const { data: availableDataTypes = [] } = useDataTypesGetCollectionQuery();
  const [putMBusDataChannel, { isLoading }] = usePutMBusDataChannelMutation();
  const [initialValues, setInitialValues] = useState(() =>
    getInitialMBusDataChannelValues(channel),
  );
  const [formValues, setFormValues] = useState(initialValues);

  const displayName = channel.displayName?.trim() || channel.channelKey?.trim();
  const dataTypeOptions = Array.from(
    new Set([
      ...availableDataTypes.map((dataType) => dataType.Key.trim()),
      ...((channel.potentialDataTypes ?? []).filter((item): item is string =>
        Boolean(item?.trim()),
      ) ?? []),
      formValues.targetDataType.trim(),
    ]),
  )
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));
  const currentlyProducingTypes = (
    channel.currentlyProducingDataTypes ?? []
  ).filter((item): item is string => Boolean(item?.trim()));
  const hasPresetDataTypeOptions = dataTypeOptions.length > 0;
  const hasChanges = hasMBusDataChannelDiff(formValues, initialValues);

  const updateField = (
    field: keyof MBusDataChannelFormValues,
    value: string,
  ) => {
    setFormValues((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!deviceId || !channel.channelKey) {
      return;
    }

    const payload = buildMBusDataChannelPayload(formValues);

    const result = await putMBusDataChannel({
      deviceId,
      channelKey: channel.channelKey,
      putMBusDataChannelDto: payload,
    });

    if (!('error' in result)) {
      setInitialValues(formValues);

      dispatch(
        showSnackbar({
          message: t('devices.capabilityUpdatedSuccess'),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">
            {displayName || t('devices.channel', { defaultValue: 'Channel' })}
          </Typography>
          {channel.channelKey ? (
            <Typography variant="caption" color="text.secondary">
              {channel.channelKey}
            </Typography>
          ) : null}
        </Stack>

        {channel.mBusDataType ? (
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t('devices.mBusDataType', { defaultValue: 'M-Bus data type' })}
            </Typography>
            <Typography variant="body2">{channel.mBusDataType}</Typography>
          </Box>
        ) : null}

        {currentlyProducingTypes.length > 0 ? (
          <Box>
            <Typography variant="overline" color="text.secondary">
              {t('devices.currentlyProducingDataTypes', {
                defaultValue: 'Currently producing data types',
              })}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              {currentlyProducingTypes.map((dataType) => (
                <Chip key={dataType} size="small" label={dataType} />
              ))}
            </Stack>
          </Box>
        ) : null}

        {hasPresetDataTypeOptions ? (
          <TextField
            select
            fullWidth
            label={t('devices.targetDataType', {
              defaultValue: 'Target data type',
            })}
            value={formValues.targetDataType}
            onChange={(event) =>
              updateField('targetDataType', event.target.value)
            }
          >
            <MenuItem value="">{t('common.notAvailable')}</MenuItem>
            {dataTypeOptions.map((dataType) => (
              <MenuItem key={dataType} value={dataType}>
                {dataType}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            fullWidth
            label={t('devices.targetDataType', {
              defaultValue: 'Target data type',
            })}
            value={formValues.targetDataType}
            onChange={(event) =>
              updateField('targetDataType', event.target.value)
            }
          />
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            type="number"
            label={t('sensors.dataConversionFactor', 'Conversion Factor')}
            value={formValues.conversionFactor}
            onChange={(event) =>
              updateField('conversionFactor', event.target.value)
            }
          />
          <TextField
            fullWidth
            type="number"
            label={t('sensors.dataConversionOffset', 'Conversion Offset')}
            value={formValues.conversionOffset}
            onChange={(event) =>
              updateField('conversionOffset', event.target.value)
            }
          />
        </Stack>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            disabled={
              isLoading || !deviceId || !channel.channelKey || !hasChanges
            }
          >
            {t('common.save')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function DataIntervalCapabilityEditor({
  deviceId,
  capability,
  onSubmitted,
}: CapabilityEditorProps<GetDataIntervalConfigurationCapabilityDto>) {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [putDataIntervalConfiguration, { isLoading }] =
    usePutDataIntervalConfigurationMutation();
  const [initialDataInterval, setInitialDataInterval] = useState(() =>
    getInitialDataInterval(capability),
  );

  const form = useForm({
    defaultValues: {
      dataInterval: initialDataInterval,
    },
    onSubmit: async ({ value }) => {
      if (!deviceId) {
        return;
      }

      const result = await putDataIntervalConfiguration({
        deviceId,
        putDataIntervalConfigurationDto: {
          dataInterval: value.dataInterval,
        },
      });

      if (!('error' in result)) {
        setInitialDataInterval(value.dataInterval);

        dispatch(
          showSnackbar({
            message: t('devices.capabilityUpdatedSuccess'),
            severity: 'success',
          }),
        );

        onSubmitted?.();
      }
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
    },
  });

  return (
    <form.Subscribe selector={(state) => state.values.dataInterval}>
      {(selectedDataInterval) => (
        <DeviceActionFormLayout
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
          isSubmitting={isLoading}
          submitLabel={t('common.save')}
          isSubmitDisabled={
            !deviceId || selectedDataInterval === initialDataInterval
          }
        >
          <form.Field name="dataInterval">
            {(field) => (
              <TextField
                select
                fullWidth
                label={t('devices.dataInterval')}
                value={field.state.value}
                onChange={(event) =>
                  field.handleChange(event.target.value as DataInterval)
                }
              >
                {DATA_INTERVAL_OPTIONS.map((intervalOption) => (
                  <MenuItem key={intervalOption} value={intervalOption}>
                    {intervalOption}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </form.Field>
        </DeviceActionFormLayout>
      )}
    </form.Subscribe>
  );
}

function MBusCapabilityEditor({
  deviceId,
  capability,
  onSubmitted,
}: CapabilityEditorProps<GetMBusSensorCapabilityDto>) {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [putMBus, { isLoading }] = usePutMBusMutation();
  const dataChannels = capability.dataChannels ?? [];
  const [initialValues, setInitialValues] = useState(() => ({
    wireless: Boolean(capability.wireless),
    mBusDeviceId: capability.mBusDeviceId ?? '',
    encryptionKey: capability.encryptionKey ?? '',
  }));

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      if (!deviceId) {
        return;
      }

      const result = await putMBus({
        deviceId,
        putMBusDto: {
          wireless: value.wireless,
          mBusDeviceId: value.mBusDeviceId.trim() || null,
          encryptionKey: value.encryptionKey.trim() || null,
        },
      });

      if (!('error' in result)) {
        setInitialValues(value);

        dispatch(
          showSnackbar({
            message: t('devices.capabilityUpdatedSuccess'),
            severity: 'success',
          }),
        );

        onSubmitted?.();
      }
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
    },
  });

  return (
    <Stack spacing={3}>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
        noValidate
      >
        <form.Subscribe selector={(state) => state.values}>
          {(values) => {
            const hasChanges = hasMBusCapabilityDiff(values, initialValues);

            return (
              <Stack spacing={2}>
                <form.Field name="wireless">
                  {(field) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.state.value}
                          onChange={(event) =>
                            field.handleChange(event.target.checked)
                          }
                        />
                      }
                      label={t('devices.mBusWireless', {
                        defaultValue: 'Wireless M-Bus',
                      })}
                    />
                  )}
                </form.Field>

                <form.Field name="mBusDeviceId">
                  {(field) => (
                    <TextField
                      fullWidth
                      label={t('devices.mBusDeviceId', {
                        defaultValue: 'M-Bus device ID',
                      })}
                      value={field.state.value}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                  )}
                </form.Field>

                <form.Field name="encryptionKey">
                  {(field) => (
                    <TextField
                      fullWidth
                      label={t('devices.encryptionKey', {
                        defaultValue: 'Encryption key',
                      })}
                      value={field.state.value}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      helperText={t('devices.encryptionKeyOptionalHelper', {
                        defaultValue:
                          'Leave empty when encryption is not required.',
                      })}
                    />
                  )}
                </form.Field>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading || !deviceId || !hasChanges}
                  >
                    {t('common.save')}
                  </Button>
                </Stack>
              </Stack>
            );
          }}
        </form.Subscribe>
      </Box>

      {dataChannels.length > 0 ? (
        <Stack spacing={2}>
          <Typography variant="subtitle2">
            {t('devices.mBusDataChannels', {
              defaultValue: 'M-Bus data channels',
            })}
          </Typography>

          {dataChannels.map((channel) => (
            <MBusDataChannelEditor
              key={
                channel.channelKey ??
                channel.dataChannelId ??
                channel.displayName
              }
              deviceId={deviceId}
              channel={channel}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function DeviceCapabilityEditorRenderer({
  deviceId,
  capability,
  policy,
  onSubmitted,
}: CapabilityEditorRendererProps) {
  switch (policy.editHandler) {
    case 'putDataIntervalConfiguration':
      return (
        <DataIntervalCapabilityEditor
          deviceId={deviceId}
          capability={capability as GetDataIntervalConfigurationCapabilityDto}
          onSubmitted={onSubmitted}
        />
      );
    case 'putMBus':
      return (
        <MBusCapabilityEditor
          deviceId={deviceId}
          capability={capability as GetMBusSensorCapabilityDto}
          onSubmitted={onSubmitted}
        />
      );
    default:
      return null;
  }
}
